import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'
import { useTranslation } from '../i18n'
import type { DashboardSummary } from '../types'
import { useTheme } from '../theme'

interface StatusDistributionProps {
  data: DashboardSummary | null
  loading: boolean
}

const COLORS = {
  passed: 'var(--color-success)',
  failed: 'var(--color-error)',
  error: '#ff6b6b',
  skipped: 'var(--color-warning)',
  unknown: 'var(--color-gray-500)',
}

export function StatusDistribution({ data, loading }: StatusDistributionProps) {
  const { t } = useTranslation()
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'

  if (loading) {
    return (
      <div className="card" data-testid="status-distribution">
        <h2 className="card-title">{t('component.statusDistribution')}</h2>
        <div
          style={{
            height: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-gray-400)',
          }}
        >
          {t('label.loading')}
        </div>
      </div>
    )
  }

  if (!data || data.totalTests === 0) {
    return (
      <div className="card" data-testid="status-distribution">
        <h2 className="card-title">{t('component.statusDistribution')}</h2>
        <div
          style={{
            height: '300px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-gray-400)',
          }}
        >
          {t('empty.noTestData')}
        </div>
      </div>
    )
  }

  const chartData = [
    { name: t('status.passed'), value: data.statusCounts.passed, color: COLORS.passed },
    { name: t('status.failed'), value: data.statusCounts.failed, color: COLORS.failed },
    { name: t('status.error'), value: data.statusCounts.error, color: COLORS.error },
    { name: t('status.skipped'), value: data.statusCounts.skipped, color: COLORS.skipped },
    { name: t('status.unknown'), value: data.statusCounts.unknown, color: COLORS.unknown },
  ].filter((d) => d.value > 0)

  return (
    <div className="card" data-testid="status-distribution">
      <h2 className="card-title">{t('component.statusDistribution')}</h2>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            labelLine={false}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: isDark ? '#1f2937' : '#ffffff',
              border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
              borderRadius: '8px',
              color: isDark ? '#f9fafb' : '#111827',
            }}
            labelStyle={{ color: isDark ? '#f9fafb' : '#111827' }}
            itemStyle={{ color: isDark ? '#f9fafb' : '#111827' }}
          />
          <Legend wrapperStyle={{ color: isDark ? '#f9fafb' : '#111827' }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}
